// Why a Run stops, as the api records it (#570): `stop:<code> <parameters as JSON>`, worded here in the person's
// language from the web catalogue. A reason without a code (a problem in words, or one recorded before codes) is
// shown as it is, and so is a code this Cockpit does not know.
import type { ShortLine, StopParameters, Words } from "../i18n/en.js";

const CODED = /^stop:([a-z_]+) (\{.*\})$/s;

type Fields = Readonly<Record<string, unknown>>;
const isFields = (value: unknown): value is Fields => typeof value === "object" && value !== null;
const isText = (value: unknown): value is string => typeof value === "string";
const isNumber = (value: unknown): value is number => typeof value === "number" && Number.isFinite(value);
const isOptional = <T>(value: unknown, is: (v: unknown) => v is T): value is T | undefined =>
  value === undefined || is(value);

const isShortLine = (line: unknown): line is ShortLine => {
  if (!isFields(line)) return false;
  const pr = line["requisition"];
  return (
    isText(line["name"]) &&
    isText(line["unit"]) &&
    isNumber(line["needs"]) &&
    isNumber(line["on_hand"]) &&
    isNumber(line["short"]) &&
    (pr === undefined ||
      (isFields(pr) && isText(pr["number"]) && isNumber(pr["quantity"]) && isOptional(pr["supplier"], isText)))
  );
};

/** The words of a coded reason, or undefined when its parameters are not the code's. */
function worded(words: Words, code: string, p: Fields): string | undefined {
  switch (code) {
    case "no_committed_date":
      return isText(p["order"]) ? words.stopNoCommittedDate(p as StopParameters["no_committed_date"]) : undefined;
    case "no_bill_of_materials": {
      const items = p["items"];
      return isText(p["order"]) && Array.isArray(items) && items.length > 0 && items.every(isText)
        ? words.stopNoBillOfMaterials(p as StopParameters["no_bill_of_materials"])
        : undefined;
    }
    case "material_short": {
      const lines = p["lines"];
      return isText(p["order"]) &&
        isText(p["due"]) &&
        Array.isArray(lines) &&
        lines.length > 0 &&
        lines.every(isShortLine)
        ? words.stopMaterialShort(p as StopParameters["material_short"])
        : undefined;
    }
    case "no_gst_rate":
      return isText(p["item"]) &&
        isText(p["hsn"]) &&
        isOptional(p["invoice"], isText) &&
        isOptional(p["stale"], isNumber)
        ? words.stopNoGstRate(p as StopParameters["no_gst_rate"])
        : undefined;
    case "already_billed": {
      const invoices = p["invoices"];
      return isText(p["order"]) && Array.isArray(invoices) && invoices.length > 0 && invoices.every(isText)
        ? words.stopAlreadyBilled(p as StopParameters["already_billed"])
        : undefined;
    }
    default:
      return undefined;
  }
}

/** A recorded reason in `words`: a coded one worded, any other as it is. */
export function stopReasonIn(words: Words, recorded: string): string {
  const [, code = "", json = ""] = CODED.exec(recorded) ?? [];
  if (code === "") return recorded;
  let parameters: unknown;
  try {
    parameters = JSON.parse(json);
  } catch {
    return recorded;
  }
  return (isFields(parameters) ? worded(words, code, parameters) : undefined) ?? recorded;
}
